/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VoicemailBehaviorBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Voicemail Settings module.
----------------------------------------------------------------*/


import { Fragment } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import Toggle from '../../../components/ui/Toggle';
import { useI18n } from '../../../hooks/useI18n';
import { VoicemailSettingsFormData } from '../types';

type ToggleField = { field: keyof VoicemailSettingsFormData; ttKey: string; lblKey: string };

interface Props {
    formData: VoicemailSettingsFormData;
    isLoading: boolean;
    onFieldChange: (field: keyof VoicemailSettingsFormData, value: unknown) => void;
}

export function VoicemailBehaviorBox({ formData, isLoading, onFieldChange }: Props) {
    const { t } = useI18n();

    const toggles: ToggleField[] = [
        { field: 'moveHeardMsg',    ttKey: 'voicemailSettings.fields.moveHeardMsg.tooltip',    lblKey: 'voicemailSettings.fields.moveHeardMsg.label' },
        { field: 'forceName',       ttKey: 'voicemailSettings.fields.forceName.tooltip',       lblKey: 'voicemailSettings.fields.forceName.label' },
        { field: 'forceGreetings',  ttKey: 'voicemailSettings.fields.forceGreetings.tooltip',  lblKey: 'voicemailSettings.fields.forceGreetings.label' },
        { field: 'useDirectory',    ttKey: 'voicemailSettings.fields.useDirectory.tooltip',    lblKey: 'voicemailSettings.fields.useDirectory.label' },
        { field: 'reviewMsg',       ttKey: 'voicemailSettings.fields.reviewMsg.tooltip',       lblKey: 'voicemailSettings.fields.reviewMsg.label' },
    ];

    return (
        <FormBox title={t('voicemailSettings.sections.behavior')} description={t('voicemailSettings.sections.behaviorDescription')} icon={<SettingsIcon className="h-5 w-5" />} isLoading={isLoading}>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-4 items-center">
                {toggles.map(({ field, ttKey, lblKey }) => (
                    <Fragment key={field}>
                        <SimpleTooltip content={t(ttKey)} className="col-span-2">
                            <Label htmlFor={field as string} className="text-left">{t(lblKey)}</Label>
                        </SimpleTooltip>
                        <div className="col-span-2">
                            <Toggle id={field as string} value={formData[field] as boolean} onChange={(v) => onFieldChange(field, v)} />
                        </div>
                    </Fragment>
                ))}
            </div>
        </FormBox>
    );
}
